import React, { useEffect, useState, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  TextField,
  Grid,
  Pagination,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Toolbar,
  Slide
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import LaunchIcon from '@mui/icons-material/Launch';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import Nodata from '../../../assets/nodata.png';
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { makeApiRequest } from '../../../utils/api';
import { formatDate } from '../../../helpers/timeHelper';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProjectStudentReview = ({ projectData, onBack }) => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [assignment, setAssignment] = useState([]);
  const [error, setError] = useState();
  const navigate = useNavigate();
  const { id } = location.state || {};
  const [openModal, setOpenModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [fileDescription, setFileDescription] = useState("");
  const [open, setOpen] = useState(false);

  const handleSubmit = () => {
    onBack();
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleBack = () => {
    navigate('/Submit-Assignment');
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setLoading(true);
    const fetchAssignmentData = async () => {
      try {
        const response = await makeApiRequest('post', '/api/assignments/id/list', {
          token: localStorage.getItem('authToken'),
          id: id
        });

        if (!response || response.status !== 'success' || !response.data) {
          throw new Error(response?.message || 'Invalid response from API');
        }
        setAssignment(response.data[0]);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching assignment data:', err);
        setError(err.message || 'Error occurred while fetching assignment data');
      }
    };
    fetchAssignmentData();
  }, [id]);
  const handleUpload = async () => {
    if (files.length === 0) {
      toast.warn("Please select files to upload.");
      return;
    }

    if (!fileDescription.trim()) {
      toast.warn("Please enter a file description");
      return;
    }

    setLoading(true);
    try {
      const base64Files = await convertFilesToBase64(files);
      const token = localStorage.getItem('authToken');

      const payload = {
        token,
        data: {
          id: id,
          stud_id: localStorage.getItem('userid'),
          input_files: base64Files,
          file_description: fileDescription,
        },
      };
      const response = await makeApiRequest('post', '/api/assignments/submit', payload);
      if (response && response.status === 'success') {
        toast.success("Assignment submitted successfully!");
        navigate('/Review-StudentAssignment', { state: { id: id } });
      }
      setFiles([]);
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to upload assignment.");
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (fileName) => {
    setFiles(files.filter((file) => file.name !== fileName));
  };

  const convertFilesToBase64 = async (files) => {
    const base64Files = await Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      })
    );
    return base64Files;
  };

  const handleOpenModal = (assignment) => {
    setSelectedAssignment(assignment);
    setFiles([]);
    setUploadStatus({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: true });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{ color: '#481E82' }}
        />
      </Box>
    );
  }


  return (
    <Box sx={{ maxWidth: '100%', minHeight: '100vh', p: '24px', backgroundColor: "#F1F4F9", fontFamily: "Roboto", display: "flex", gap: 3 }}>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="light"
      />
      <Box sx={{ display: "flex", alignItems: "flex-start", backgroundColor: "#F1F4F9" }}>
        <IconButton
          onClick={handleBack}
          sx={{
            border: "2px solid #1567C1",
            borderRadius: '4px',
            width: 40,
            height: 40,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingLeft: 2,
            margin: 0,
            minWidth: "auto",
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ width: "100%", height: "100%" }}>
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#fff',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            borderRadius: 1
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Student Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "20px" }}>
                {assignment?.stud_name || 'N/A'}
                <Typography style={{ fontSize: "12px", color: "#0091ea" }}>
                  {assignment?.stud_id || 'N/A'}
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Batch / Course
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "20px" }}>
                {assignment?.batch_name || 'N/A'}
                <Typography style={{ fontSize: "12px", color: "#0091ea" }}>
                  {assignment?.course_name || 'N/A'}
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Module / Chapter
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "20px" }}>
                {assignment?.module_name || 'N/A'}
                <Typography style={{ fontSize: "12px", color: "#0091ea" }}>
                  {assignment?.chapter_name || 'N/A'}
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grid container>
                <Grid item xs={assignment.status === "reviewed" ? 6 : 12}>
                  <Typography variant="caption" color="textSecondary">
                    Status
                  </Typography>
                  <br />
                  <Button
                    sx={{
                      bgcolor: {
                        reviewed: "#D4BBFC",
                        assigned: "#FFFFA3",
                        submitted: "#CBFAF0",
                        rejected: "#FDE6EB",
                      }[assignment.status] || "#CBFAF0",
                      color: {
                        reviewed: "#421869",
                        assigned: "#FF8000",
                        submitted: "#298880",
                        rejected: "#BA3068",
                      }[assignment.status] || "#000000",
                      height: 50,
                      minWidth: 80,
                      borderRadius: "4px",
                      border: `1px solid ${{
                        reviewed: "#9667E0",
                        assigned: "#FF9900",
                        submitted: "#98EEDB",
                        rejected: "#FCD6DE",
                      }[assignment.status] || "#98EEDB"}`,
                      textTransform: "capitalize",
                      fontSize: "20px",
                      padding: "2px 8px",
                      "&:hover": {
                        bgcolor: {
                          reviewed: "#E6D1FF",
                          assigned: "#FFFFCC",
                          submitted: "#E1FFF8",
                          rejected: "#FFE8F0",
                        }[assignment.status] || "#D6F7F0",
                      },
                    }}
                    size="small"
                  >
                    {assignment.status === "assigned" ? "Pending" : assignment.status}
                  </Button>
                </Grid>
                {assignment.status === "reviewed" && (
                  <Grid item xs={6} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Paper elevation={3} sx={{ backgroundColor: "#FFEE99", p: 2, textAlign: "center" }}>
                      <Typography variant="caption" color="textSecondary">
                        Marks
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: "#1976d2" }}>
                        {assignment.mark}/10
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Source File Location
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "14px", wordWrap: "break-word" }}>
                {assignment.source_file_path}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Project Title
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "14px", textWrap: "wrap" }}>
                {assignment.project_title}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Project Description
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "14px", wordWrap: "break-word" }}>
                {assignment.project_description}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Assigned By
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "20px" }}>
                {assignment.created_by}
                <Typography>
                  on <span style={{ fontSize: "14px", color: "#0091ea" }}>{formatDate(assignment.assign_date)}</span>
                </Typography>
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper elevation={4} sx={{ borderRadius: "14px" }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  File Description
                </Typography>
                {(assignment.status === "assigned" || assignment.status === "rejected") && (
                  <IconButton size="small">
                    <UploadFileIcon sx={{ fontSize: 20, color: "#3d8bd8", border: "1px solid #1976D2", p: 1, borderRadius: "5px" }} onClick={handleOpenModal} />
                  </IconButton>
                )}
                {(assignment.status === "reviewed" || assignment.status === "submitted") && (
                  <IconButton size="small">
                    <LaunchIcon onClick={handleClickOpen} sx={{ fontSize: 20, color: "#3d8bd8" }} />
                  </IconButton>
                )}
              </Box>
              <Typography variant="body1" sx={{ px: 3, pb: 2 }}>
                {assignment.file_description}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  minHeight: "250px",
                  bgcolor: "#EEEEEE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                  overflow: "hidden",
                  mb: 2
                }}
              >
                {assignment.status === "assigned" && (
                  <Box
                    component="img"
                    src={Nodata}
                    sx={{ width: "80%", height: "250px", objectFit: "contain" }}
                  />
                )}
                {(assignment.status === "submitted" || assignment.status === "reviewed" || assignment.status === "rejected") && (
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: "150px",
                      bgcolor: "#EEEEEE",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1,
                      overflow: "hidden",
                      mb: 2
                    }}
                  >
                    {assignment.input_file_path && assignment.input_file_path.length > 0 ? (
                      (() => {
                        const fileUrl = `https://learn.quraltalent.com/documents/${assignment.input_file_path[currentPage - 1]}`;
                        // const fileUrl = `http://127.0.0.1:8000/documents/${assignment.input_file_path[currentPage - 1]}`;
                        // const fileUrl = `http://16.171.0.8/documents/${assignment.input_file_path[currentPage - 1]}`;

                        const fileExtension = fileUrl.split('.').pop().toLowerCase();

                        if (["png", "jpg", "jpeg", "gif"].includes(fileExtension)) {
                          return (
                            <img
                              src={fileUrl}
                              alt="Uploaded file"
                              style={{ width: "80%", height: "250px", objectFit: "contain" }}
                            />
                          );
                        } else if (["mp3", "wav", "ogg", "m4a", "aac"].includes(fileExtension)) {
                          return (
                            <Box sx={{
                              width: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 3
                            }}>
                              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Audio File: {assignment.input_file_path[currentPage - 1].split('/').pop()}
                              </Typography>
                              <audio
                                controls
                                style={{ width: "100%", maxWidth: "400px" }}
                                src={fileUrl}
                              >
                                Your browser does not support the audio element.
                              </audio>
                            </Box>
                          );
                        } else if (fileExtension === "pdf") {
                          return (
                            <iframe
                              src={fileUrl}
                              style={{ width: "100%", height: "400px", border: "none" }}
                              title="PDF Viewer"
                            />
                          );
                        } else if (["txt", "csv", "log"].includes(fileExtension)) {
                          return (
                            <iframe
                              src={fileUrl}
                              style={{ width: "100%", height: "400px", border: "none" }}
                              title="Text Viewer"
                            />
                          );
                        } else if (["ppt", "pptx"].includes(fileExtension)) {
                          return (
                            <iframe
                              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
                              style={{ width: "100%", height: "600px", border: "none" }}
                              title="PowerPoint Viewer"
                            />
                          );
                        } else {
                          return <Typography>No preview available for this file type.</Typography>;
                        }
                      })()
                    ) : (
                      <Typography>No file available</Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 3 }}>
                {(assignment.status === "submitted" || assignment.status === "reviewed" || assignment.status === "rejected") && (
                  <Stack spacing={2}>
                    <Pagination
                      count={assignment.input_file_path?.length || 0}
                      page={currentPage}
                      showFirstButton
                      showLastButton
                      onChange={handlePageChange}
                      sx={{
                        '& .MuiPaginationItem-root': { mx: 0.5 },
                        '& .Mui-selected': {
                          bgcolor: '#9c27b0 !important',
                          color: 'white',
                          borderRadius: "50%"
                        }
                      }}
                    />
                  </Stack>
                )}
              </Box>
            </Paper>
          </Grid>
          {(assignment.status === "reviewed" || assignment.status === "rejected") && (
            <Grid item xs={12} md={7}>
              <Box sx={{ px: 3, py: 3, borderRadius: "14px", boxShadow: 2, bgcolor: '#ECECEC' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography color="#481E82" sx={{ fontSize: '20px' }}>
                      <strong style={{ fontWeight: 500 }}>Assign Marks</strong>
                    </Typography>
                    <Divider sx={{ mt: 2 }} />
                  </Grid>
                  {assignment.status === "reviewed" && (
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <TextField
                              fullWidth
                              label="Status"
                              name="status"
                              value={assignment.status}
                              size="small"
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  backgroundColor: "white",
                                  "& input": { textTransform: "capitalize" }
                                },
                                readOnly: true
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <TextField
                              fullWidth
                              label="Mark"
                              name="mark"
                              value={`${assignment.mark}/10`}
                              size="small"
                              variant="outlined"
                              InputProps={{
                                sx: { backgroundColor: "white" },
                                readOnly: true
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box sx={{ mb: 3, width: "100%" }}>
                      <TextField
                        multiline
                        rows={3}
                        fullWidth
                        label="Remarks"
                        name="remarks"
                        value={assignment?.remark || 'No Remarks'}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#fff',
                            paddingBottom: "40px",
                          },
                        }}
                        InputProps={{ readOnly: true }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          <Typography sx={{ fontSize: "15px", color: "grey" }}>Project Title</Typography>
          <Typography variant='h4'>{assignment.project_title}</Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers sx={{ backgroundColor: "#ececec" }}>
          <Box>
            <TextField
              fullWidth
              label="File Description"
              variant="outlined"
              margin="normal"
              required
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
              size='small'
              placeholder='Enter File Description'
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { backgroundColor: "white" } }}
              sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
            />
          </Box>
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #1976d2",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#dbe2ea",
              marginTop: "10px",
              marginBottom: "10px",
              height: "100px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column"
            }}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography color="primary">Drop the files here...</Typography>
            ) : (
              <Box>
                <UploadFileIcon color='primary' />
                <Typography>Drag & Drop</Typography>
                <Typography>or <span style={{ color: "#1976d2", textDecoration: "underline" }}>choose a file</span></Typography>
              </Box>
            )}
          </Box>
          {files.length > 0 && (
            <div>
              {files.map((file, index) => (
                <div key={index} style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  marginBottom: "5px",
                  backgroundColor: "white",
                  padding: "10px",
                  borderRadius: "10px"
                }}>
                  <Box sx={{ display: "flex", gap: "15px", alignItems: "center" }}>
                    <UploadFileIcon color="primary" />
                    <Box>
                      <Typography>{file.name}</Typography>
                      <Typography>({(file.size / 1024).toFixed(1)} KB)</Typography>
                    </Box>
                  </Box>
                  {uploadStatus[file.name] === "loading" && <Typography color="blue">Loading...</Typography>}
                  {uploadStatus[file.name] === "complete" && <Typography color="green">✔ Complete</Typography>}
                  {uploadStatus[file.name] === "failed" && <Typography color="red">✖ Failed (Too Large)</Typography>}
                  <Box>
                    <IconButton onClick={() => removeFile(file.name)} color="error">
                      <DeleteIcon />
                    </IconButton>
                    <IconButton color='success'>
                      <CheckCircleIcon />
                    </IconButton>
                  </Box>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "25px" }}>
          <Button onClick={handleUpload} variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <Paper elevation={4} sx={{ borderRadius: "0px" }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: "0!important" }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              File Description
            </Typography>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </Box>
          <Typography variant="body1" sx={{ px: 3, pb: 2 }}>
            {assignment.file_description}
          </Typography>
          <Box
            sx={{
              width: "100%",
              bgcolor: "#f1f4f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              mb: 2
            }}
          >
            {assignment.status === "assigned" && (
              <Box
                component="img"
                src={Nodata}
                sx={{ width: "80%", height: "100vh", objectFit: "contain" }}
              />
            )}
            {(assignment.status === "submitted" || assignment.status === "reviewed" || assignment.status === "rejected") && (
              <Box
                sx={{
                  width: "80%",
                  height: "100vh",
                  objectFit: "contain"
                }}
              >
                {assignment.input_file_path && assignment.input_file_path.length > 0 ? (
                  (() => {
                    const fileUrl = `https://learn.quraltalent.com/documents/${assignment.input_file_path[currentPage - 1]}`;
                    // const fileUrl = `http://127.0.0.1:8000/documents/${assignment.input_file_path[currentPage - 1]}`;
                    // const fileUrl = `http://16.171.0.8/documents/${assignment.input_file_path[currentPage - 1]}`;

                    const fileExtension = fileUrl.split('.').pop().toLowerCase();

                    if (["png", "jpg", "jpeg", "gif"].includes(fileExtension)) {
                      return (
                        <img
                          src={fileUrl}
                          alt="Uploaded file"
                          style={{ width: "80%", height: "250px", objectFit: "contain" }}
                        />
                      );
                    } else if (["mp3", "wav", "ogg", "m4a", "aac"].includes(fileExtension)) {
                      return (
                        <Box sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 3
                        }}>
                          <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Audio File: {assignment.input_file_path[currentPage - 1].split('/').pop()}
                          </Typography>
                          <audio
                            controls
                            style={{ width: "100%", maxWidth: "400px" }}
                            src={fileUrl}
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </Box>
                      );
                    } else if (fileExtension === "pdf") {
                      return (
                        <iframe
                          src={fileUrl}
                          style={{ width: "100%", height: "100%", border: "none" }}
                          title="PDF Viewer"
                        />
                      );
                    } else if (["txt", "csv", "log"].includes(fileExtension)) {
                      return (
                        <iframe
                          src={fileUrl}
                          style={{ width: "100%", height: "400px", border: "none" }}
                          title="Text Viewer"
                        />
                      );
                    } else if (["ppt", "pptx"].includes(fileExtension)) {
                      return (
                        <iframe
                          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
                          style={{ width: "100%", height: "600px", border: "none" }}
                          title="PowerPoint Viewer"
                        />
                      );
                    } else {
                      return <Typography>No preview available for this file type.</Typography>;
                    }
                  })()
                ) : (
                  <Typography>No file available</Typography>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 1 }}>
            {(assignment.status === "submitted" || assignment.status === "reviewed" || assignment.status === "rejected") && (
              <Stack spacing={2}>
                <Pagination
                  count={assignment.input_file_path?.length || 0}
                  page={currentPage}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  sx={{
                    '& .MuiPaginationItem-root': { mx: 0.5 },
                    '& .Mui-selected': {
                      bgcolor: '#9c27b0 !important',
                      color: 'white',
                      borderRadius: "50%"
                    }
                  }}
                />
              </Stack>
            )}
          </Box>
        </Paper>
      </Dialog>
    </Box>
  );
}


export default ProjectStudentReview;